import { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import { useCreateTenantUser } from "../../data/useCreateTenantUser";
import { useOtherUserDetails } from "../../data/useOtherUserDetails";
import { usePermissionGroups } from "../../data/usePermissionGroups";
import { useUpdateUserDetails } from "../../data/useUpdateUserDetails";
import { useDivisions } from "../../data/useDivisions";
import { useTeams } from "../../data/useTeams";
import { useAccessRoles } from "../../data/useAccessRoles";
import { useUserRoleAssignments } from "../../data/useUserRoleAssignments";
import { useCreateUserRole } from "../../data/useCreateUserRole";
import { useBulkUpdateUserRoles } from "../../data/useBulkUpdateUserRoles";
import { useDeleteUserRole } from "../../data/useDeleteUserRole";
import { queryClient } from "../../queryClient";
import { showError } from "../../utils/error-handling";
import { notify } from "../../utils/notify";
import { unwrapList } from "../../utils/unwrapList";
import { AccessRole } from "../../types/AccessRole";
import { Box } from "../Box";
import { Button } from "../Button";
import { Flex } from "../Flex";
import { FormRow } from "../Form";
import { Input, InputProps } from "../Input";
import { Loader } from "../Loader";
import { Slider } from "../Slider";
import { Paragraph } from "../Paragraph";
import { ExpansionPanel } from "../ExpansionPanel";
import { Toggle } from "../Toggle";
import CustomTabs from "../CustomTabs";
import Select, { Option } from "../Select";
import { ConfirmModal } from "./ConfirmModal";
import { addUserValidation } from "../../utils/validationSchema";
import { useAuth } from "../../context/AuthContext";

export const AddEditUserModal = (props: any) => {
  const { isOpen, onRequestClose, userId } = props;
  const mode = typeof userId === "number" ? "edit" : "create";
  const { hasPermission } = useAuth();

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (isOpen) setActiveTab(0);
  }, [isOpen, userId]);

  const { mutate: createUser, isLoading: isLoadingCreate } =
    useCreateTenantUser();
  const { mutate: updateUser, isLoading: isLoadingUpdate } =
    useUpdateUserDetails();
  const { data: user, isLoading: isLoadingUserDetails } = useOtherUserDetails(
    userId,
    mode === "edit"
  );

  const { data: permissions } = usePermissionGroups();
  const { data: divisions } = useDivisions();
  const { data: teams } = useTeams();

  const permissionGroupOptions = permissions?.data?.results?.map(
    ({ id, name }) => ({
      label: name,
      value: id,
    })
  ) as Option[];

  // SEG Access Role assignment — separate from the legacy `groups`
  // Permission above, and from a plain Role field on the user record: the
  // assignment lives in its own join-table endpoint (`/user/user_role/`),
  // so it's fetched and synced independently of the main user payload. A
  // toggle-list of every Role (mirroring the "Assigned users" tab on a
  // Role's own edit slider), not a single-select — a user can hold more
  // than one Role.
  const { data: accessRolesData } = useAccessRoles();
  const accessRoles = unwrapList<AccessRole>(accessRolesData?.data).filter(
    (accessRole) => !accessRole.is_deleted
  );

  const {
    data: userRoleAssignmentsData,
    isLoading: isLoadingUserRoleAssignments,
  } = useUserRoleAssignments(mode === "edit" ? userId : undefined);
  // `role` on a `?user=`-filtered row isn't confirmed as bare id vs nested
  // object (see useUserRoleAssignments.ts) — read either.
  const normalizeAssignmentRoleId = (role: any) =>
    typeof role === "object" ? role?.id : role;
  const userRoleAssignments = unwrapList<any>(userRoleAssignmentsData?.data);
  const currentRoleIds = userRoleAssignments.map((assignment) =>
    normalizeAssignmentRoleId(assignment.role)
  );
  // looked up when un-toggling a Role — DELETE needs the assignment row's
  // own id, not the Role id (see useDeleteUserRole.ts).
  const assignmentIdByRoleId = new Map<number, number>(
    userRoleAssignments.map((assignment) => [
      normalizeAssignmentRoleId(assignment.role),
      assignment.id,
    ])
  );

  const { mutateAsync: createUserRole } = useCreateUserRole();
  const { mutate: bulkUpdateUserRoles } = useBulkUpdateUserRoles();
  const { mutateAsync: deleteUserRole } = useDeleteUserRole();
  // Edit keeps the slider open across saves (Save & Next), so a second Save
  // must not start until the first one's Role changes have landed and
  // `currentRoleIds` has been refetched — otherwise it diffs against stale
  // assignments and re-POSTs / re-DELETEs the same Role.
  const [isSyncingRoles, setIsSyncingRoles] = useState(false);

  const isLoadingUser =
    mode === "edit"
      ? isLoadingUserDetails || isLoadingUserRoleAssignments
      : false;
  const isLoadingAction = isLoadingCreate || isLoadingUpdate;

  const teamsByDivision = (divisionId: number) =>
    teams?.data?.filter(({ division }) => division === divisionId) ?? [];

  // Membership changes take effect on the user's visibility immediately, so
  // an edit that adds or removes a Division/Team is confirmed first with the
  // exact gains and losses spelled out (SEG-S021). Compared against the
  // saved user record, not Formik's baseline, so Save & Next stays accurate.
  const [pendingSave, setPendingSave] = useState<{
    values: any;
    helpers: any;
    content: string;
  } | null>(null);

  const describeAccessChange = (values: any): string | null => {
    const savedDivisionIds = user?.data?.division?.map(({ id }) => id) ?? [];
    const savedTeamIds = user?.data?.team?.map(({ id }) => id) ?? [];

    const divisionName = (id: number) =>
      divisions?.data?.find((division) => division.id === id)?.display_name;
    const teamName = (id: number) =>
      teams?.data?.find((team) => team.id === id)?.display_name;

    const names = (
      ids: number[],
      savedIds: number[],
      lookup: (id: number) => string | undefined,
      kind: string
    ) =>
      ids
        .filter((id) => !savedIds.includes(id))
        .map((id) => `${lookup(id) ?? "Unknown"} (${kind})`);

    const gained = [
      ...names(values.divisionIds, savedDivisionIds, divisionName, "Division"),
      ...names(values.teamIds, savedTeamIds, teamName, "Team"),
    ];
    const lost = [
      ...names(savedDivisionIds, values.divisionIds, divisionName, "Division"),
      ...names(savedTeamIds, values.teamIds, teamName, "Team"),
    ];

    if (gained.length === 0 && lost.length === 0) return null;

    const firstName = user?.data?.first_name || "This user";
    return [
      gained.length > 0 &&
        `${firstName} will gain access to records in: ${gained.join(", ")}.`,
      lost.length > 0 &&
        `${firstName} will lose access to records in: ${lost.join(", ")}.`,
      "This takes effect immediately.",
    ]
      .filter(Boolean)
      .join(" ");
  };

  // Applies the Role toggle-list changes made on the Role tab. Can't be
  // batched into the main user payload above — Role assignment is its own
  // join-table endpoint, not a field on the user record — so this fires as a
  // best-effort follow-up once the user itself is known to exist. Errors here
  // are surfaced but don't undo the user save that already succeeded.
  //
  // Create has no existing assignments to diff against, so the whole toggled
  // set goes in one call to the bulk endpoint (`useBulkUpdateUserRoles`,
  // PATCH `/user/user_role/bulk/`, `{user, roles: number[]}`). Edit diffs
  // against `currentRoleIds` instead: each newly-toggled-on Role gets its own
  // POST (`useCreateUserRole`, `{user, role}`), and each toggled-off Role its
  // own DELETE (`useDeleteUserRole`, keyed by the assignment row's id, not the
  // Role id) — no bulk-remove endpoint is confirmed. Edit resolves once those
  // have settled and the user's assignments have been refetched.
  const syncUserRoles = async (
    targetUserId: number,
    nextRoleIds: number[]
  ): Promise<void> => {
    const invalidateAfterChange = (roleId: number) => {
      queryClient.invalidateQueries(["accessRole", roleId]);
      queryClient.invalidateQueries(["accessRoles"]);
      queryClient.invalidateQueries(["userRoleAssignments", targetUserId]);
    };

    if (mode !== "edit") {
      if (nextRoleIds.length === 0) return;

      bulkUpdateUserRoles(
        { user: targetUserId, roles: nextRoleIds },
        {
          onSuccess: () => nextRoleIds.forEach(invalidateAfterChange),
          onError: (err: any) =>
            showError(
              err,
              "The user was saved, but assigning the Role(s) failed."
            ),
        }
      );
      return;
    }

    const addedRoleIds = nextRoleIds.filter(
      (id) => !currentRoleIds.includes(id)
    );
    const removedRoleIds = currentRoleIds.filter(
      (id) => !nextRoleIds.includes(id)
    );

    const requests = [
      ...addedRoleIds.map((roleId) =>
        createUserRole({ user: targetUserId, role: roleId })
          .then(() => invalidateAfterChange(roleId))
          .catch((err: any) =>
            showError(
              err,
              "The user was saved, but assigning a new Role failed."
            )
          )
      ),
      ...removedRoleIds.map((roleId) => {
        const assignmentId = assignmentIdByRoleId.get(roleId);
        if (!assignmentId) return Promise.resolve();

        return deleteUserRole({ id: assignmentId })
          .then(() => invalidateAfterChange(roleId))
          .catch((err: any) =>
            showError(err, "The user was saved, but removing a Role failed.")
          );
      }),
    ];
    if (requests.length === 0) return;

    await Promise.all(requests);
    await queryClient.refetchQueries(["userRoleAssignments", targetUserId]);
  };

  const handleSubmit = (data, { resetForm }) => {
    const payload: any = {
      first_name: data.firstName.trim(),
      last_name: data.lastName.trim(),
      job_title: data.jobTitle.trim(),
      groups: [data.groups],
      division: data.divisionIds,
      active_division: data.divisionIds,
      team: data.teamIds,
      active_team: data.teamIds,
    };

    if (mode !== "edit") {
      payload.email = data.email;
    }

    const onComplete = () => {
      queryClient.invalidateQueries(["users"]);
      onRequestClose();
    };

    if (mode === "edit") {
      updateUser(
        {
          id: userId,
          ...payload,
        },
        {
          onSuccess: async (res) => {
            if (res?.status?.toString().startsWith("2"))
              notify("User Updated", {
                type: "success",
              });
            queryClient.invalidateQueries(["users"]);
            queryClient.invalidateQueries(["userDetails", userId]);

            setIsSyncingRoles(true);
            await syncUserRoles(userId, data.roleIds);
            setIsSyncingRoles(false);

            // Save & Next: stay open, take what was just saved as the new
            // baseline (so the button drops back to plain Next until the
            // next change), and move on. Save on the last tab closes.
            if (activeTab < 2) {
              resetForm({ values: data });
              setActiveTab(activeTab + 1);
            } else {
              onRequestClose();
            }
          },
          onError: (err: any) => {
            showError(err, "A problem occurred while updating the user.");
          },
        }
      );
    } else {
      createUser(payload, {
        onSuccess: (res) => {
          if (res?.status?.toString().startsWith("2"))
            notify("User Invited", {
              type: "success",
            });
          // the create response's id field isn't confirmed against a real
          // sample — assumed to match every other create endpoint in this
          // repo. If it's actually named differently, the Role assignment
          // silently won't fire; the user itself is still created fine.
          const newUserId = res?.data?.id;
          if (typeof newUserId === "number") {
            syncUserRoles(newUserId, data.roleIds);
          }
          onComplete();
        },
        onError: (err: any) => {
          showError(err, "A problem occurred while creating the user.");
        },
      });
    }
  };

  const inputProps: Partial<InputProps> = {
    minWidth: 200,
    flex: 1,
  };

  return (
    <Slider isOpen={isOpen} onRequestClose={onRequestClose} width="600px">
      <Box px={4} py={3} height="87%" overflowY="auto">
        {isLoadingUser && (
          <Flex justifyContent="center" alignItems="center" minHeight="25vh">
            <Loader />
          </Flex>
        )}

        {!isLoadingUser && (
          <Formik
            initialValues={{
              firstName: user?.data.first_name || "",
              lastName: user?.data.last_name || "",
              email: user?.data.email || "",
              jobTitle: user?.data.job_title || "",
              groups: user?.data?.groups?.[0]?.id || "",
              divisionIds: user?.data?.division?.map(({ id }) => id) ?? [],
              teamIds: user?.data?.team?.map(({ id }) => id) ?? [],
              roleIds: currentRoleIds,
            }}
            validationSchema={addUserValidation}
            onSubmit={(values, helpers) => {
              const accessChange =
                mode === "edit" ? describeAccessChange(values) : null;
              if (accessChange) {
                setPendingSave({ values, helpers, content: accessChange });
                return;
              }
              handleSubmit(values, helpers);
            }}
          >
            {(formikProps) => {
              const allDivisionIds = divisions?.data?.map(({ id }) => id) ?? [];
              const allTeamIds = teams?.data?.map(({ id }) => id) ?? [];
              const allSelected =
                allDivisionIds.length > 0 &&
                allDivisionIds.every((id) =>
                  formikProps.values.divisionIds.includes(id)
                ) &&
                allTeamIds.every((id) =>
                  formikProps.values.teamIds.includes(id)
                );

              const toggleDivision = (divisionId: number, checked: boolean) => {
                if (checked) {
                  formikProps.setFieldValue("divisionIds", [
                    ...formikProps.values.divisionIds,
                    divisionId,
                  ]);

                  // A Division must have at least one active Team — select
                  // them all if none under it are already selected.
                  const teamIdsInDivision = teamsByDivision(divisionId).map(
                    ({ id }) => id
                  );
                  const alreadyHasTeamSelected = teamIdsInDivision.some(
                    (id: number) => formikProps.values.teamIds.includes(id)
                  );
                  if (!alreadyHasTeamSelected && teamIdsInDivision.length > 0) {
                    formikProps.setFieldValue("teamIds", [
                      ...formikProps.values.teamIds,
                      ...teamIdsInDivision,
                    ]);
                  }
                } else {
                  formikProps.setFieldValue(
                    "divisionIds",
                    formikProps.values.divisionIds.filter(
                      (id: number) => id !== divisionId
                    )
                  );
                  const teamIdsInDivision = teamsByDivision(divisionId).map(
                    ({ id }) => id
                  );
                  formikProps.setFieldValue(
                    "teamIds",
                    formikProps.values.teamIds.filter(
                      (id: number) => !teamIdsInDivision.includes(id)
                    )
                  );
                }
              };

              const toggleTeam = (team: any, checked: boolean) => {
                if (checked) {
                  formikProps.setFieldValue("teamIds", [
                    ...formikProps.values.teamIds,
                    team.id,
                  ]);
                  if (!formikProps.values.divisionIds.includes(team.division)) {
                    formikProps.setFieldValue("divisionIds", [
                      ...formikProps.values.divisionIds,
                      team.division,
                    ]);
                  }
                } else {
                  const nextTeamIds = formikProps.values.teamIds.filter(
                    (id: number) => id !== team.id
                  );
                  formikProps.setFieldValue("teamIds", nextTeamIds);

                  // A Division can't stay active with zero active Teams —
                  // switch it off too if this was the last one.
                  const remainingTeamsInDivision = teamsByDivision(
                    team.division
                  ).filter(({ id }) => nextTeamIds.includes(id));
                  if (remainingTeamsInDivision.length === 0) {
                    formikProps.setFieldValue(
                      "divisionIds",
                      formikProps.values.divisionIds.filter(
                        (id: number) => id !== team.division
                      )
                    );
                  }
                }
              };

              const toggleAll = (checked: boolean) => {
                formikProps.setFieldValue(
                  "divisionIds",
                  checked ? allDivisionIds : []
                );
                formikProps.setFieldValue("teamIds", checked ? allTeamIds : []);
              };

              // A selected Division needs at least one selected Team under
              // it (Divisions with no Teams at all are exempt).
              const hasDivisionMissingATeam = formikProps.values.divisionIds.some(
                (divisionId: number) => {
                  const teamsInDivision = teamsByDivision(divisionId);
                  return (
                    teamsInDivision.length > 0 &&
                    !teamsInDivision.some(({ id }) =>
                      formikProps.values.teamIds.includes(id)
                    )
                  );
                }
              );

              const detailsFieldNames = [
                "firstName",
                "lastName",
                "email",
                "jobTitle",
                "groups",
              ];

              const handleNextFromDetails = async () => {
                const errors = await formikProps.validateForm();
                const hasDetailsErrors = detailsFieldNames.some(
                  (name) => (errors as any)[name]
                );
                if (hasDetailsErrors) {
                  formikProps.setTouched({
                    ...formikProps.touched,
                    ...detailsFieldNames.reduce(
                      (acc, name) => ({ ...acc, [name]: true }),
                      {}
                    ),
                  });
                  return;
                }
                setActiveTab(1);
              };

              return (
                <Form onSubmit={formikProps.handleSubmit}>
                  <Paragraph fontWeight="bold" fontSize={3} mt="0px">
                    {userId ? "Edit User" : "Invite User"}
                  </Paragraph>
                  <Box borderBottom="1px solid" borderBottomColor="gray.30"/>

                  <CustomTabs
                    showDividerBelowNav
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    tabs={[
                      {
                        label: "Details",
                        content: (
                          <Box>
                            <FormRow my={3}>
                              <Input
                                onChange={formikProps.handleChange}
                                value={formikProps.values.firstName}
                                placeholder="Enter your first name"
                                label="First name"
                                name="firstName"
                                {...inputProps}
                                isRequired
                                hasError={Boolean(formikProps.errors.firstName)}
                                errorMessage={formikProps.errors.firstName}
                              />
                              <Input
                                onChange={formikProps.handleChange}
                                value={formikProps.values.lastName}
                                placeholder="Enter your last name"
                                label="Last name"
                                name="lastName"
                                {...inputProps}
                                isRequired
                                hasError={Boolean(formikProps.errors.lastName)}
                                errorMessage={formikProps.errors.lastName}
                              />
                            </FormRow>
                            <FormRow my={3}>
                              <Input
                                onChange={formikProps.handleChange}
                                value={formikProps.values.email}
                                placeholder="Enter your email address"
                                label="Email Address"
                                name="email"
                                isDisabled={mode === "edit"}
                                {...inputProps}
                                isRequired
                                hasError={Boolean(formikProps.errors.email)}
                                errorMessage={formikProps.errors.email}
                              />
                              <Input
                                onChange={formikProps.handleChange}
                                value={formikProps.values.jobTitle}
                                placeholder="Enter your job title"
                                label="Job Title"
                                name="jobTitle"
                                {...inputProps}
                                isRequired
                                hasError={Boolean(formikProps.errors.jobTitle)}
                                errorMessage={formikProps.errors.jobTitle}
                              />
                            </FormRow>
                            <Select
                              value={
                                formikProps.values.groups
                                  ? permissionGroupOptions?.find(
                                      ({ value }) =>
                                        value === formikProps.values.groups
                                    )
                                  : null
                              }
                              options={permissionGroupOptions}
                              onChange={(option: Option) =>
                                formikProps.setFieldValue(
                                  "groups",
                                  option.value
                                )
                              }
                              label="Permissions"
                              isInModal
                              isRequired
                              hasError={Boolean(formikProps.errors.groups)}
                              errorMessage={formikProps.errors.groups}
                            />
                          </Box>
                        ),
                      },
                      {
                        label: "Divisions & Teams",
                        content: (
                          <Box>
                            <Flex justifyContent="flex-end" mb={2}>
                              <Flex alignItems="center" gap="8">
                                <Paragraph m="0px">All</Paragraph>
                                <Toggle
                                  isChecked={allSelected}
                                  isDisabled={
                                    !hasPermission("assign.division") ||
                                    !hasPermission("assign.team")
                                  }
                                  title={
                                    !hasPermission("assign.division")
                                      ? "You don't have permission to assign division membership"
                                      : !hasPermission("assign.team")
                                      ? "You don't have permission to assign team membership"
                                      : undefined
                                  }
                                  onChange={(e) =>
                                    toggleAll(e.target.checked)
                                  }
                                />
                              </Flex>
                            </Flex>

                            {divisions?.data?.map((division) => (
                              <ExpansionPanel
                                key={division.id}
                                iconPosition="start"
                                headerPy={2}
                                showHeaderDivider
                                contentMt={0}
                                title={
                                  <Flex
                                    justifyContent="space-between"
                                    alignItems="center"
                                    width="100%"
                                  >
                                    <Paragraph fontWeight="bold" m="0px">
                                      {division.display_name}
                                    </Paragraph>
                                    <Box onClick={(e) => e.stopPropagation()}>
                                      <Toggle
                                        isChecked={formikProps.values.divisionIds.includes(
                                          division.id
                                        )}
                                        isDisabled={!hasPermission("assign.division")}
                                        title={
                                          hasPermission("assign.division")
                                            ? undefined
                                            : "You don't have permission to assign division membership"
                                        }
                                        onChange={(e) =>
                                          toggleDivision(
                                            division.id,
                                            e.target.checked
                                          )
                                        }
                                      />
                                    </Box>
                                  </Flex>
                                }
                              >
                                {teamsByDivision(division.id).map((team) => (
                                  <Flex
                                    key={team.id}
                                    justifyContent="space-between"
                                    alignItems="center"
                                    py={2}
                                    borderBottom="1px solid"
                                    borderBottomColor="gray.10"
                                  >
                                    <Flex alignItems="center" gap="12">
                                      <Box width="26px" />
                                      <Paragraph m="0px" color="gray.60">
                                        {team.display_name}
                                      </Paragraph>
                                    </Flex>
                                    <Toggle
                                      isChecked={formikProps.values.teamIds.includes(
                                        team.id
                                      )}
                                      isDisabled={!hasPermission("assign.team")}
                                      title={
                                        hasPermission("assign.team")
                                          ? undefined
                                          : "You don't have permission to assign team membership"
                                      }
                                      onChange={(e) =>
                                        toggleTeam(team, e.target.checked)
                                      }
                                    />
                                  </Flex>
                                ))}
                                {teamsByDivision(division.id).length === 0 && (
                                  <Flex alignItems="center" gap="12" py={2}>
                                    <Box width="26px" />
                                    <Paragraph color="gray.50" m="0px">
                                      No teams in this division.
                                    </Paragraph>
                                  </Flex>
                                )}
                              </ExpansionPanel>
                            ))}
                          </Box>
                        ),
                      },
                      {
                        label: "Role",
                        content: (
                          <Box mt={3}>
                            <Paragraph color="gray.50" fontSize={1} mb={3}>
                              This is separate from the legacy Permission set
                              on the Details tab — both apply until SEG
                              Role-based access fully replaces Permissions.
                            </Paragraph>

                            {accessRoles.length === 0 && (
                              <Paragraph color="gray.60">
                                No Roles found.
                              </Paragraph>
                            )}

                            {accessRoles.map((accessRole) => (
                              <Flex
                                key={accessRole.id}
                                justifyContent="space-between"
                                alignItems="center"
                                py={2}
                                borderBottom="1px solid"
                                borderBottomColor="gray.10"
                              >
                                <Paragraph m="0px" color="gray.60">
                                  {accessRole.name}
                                </Paragraph>
                                <Toggle
                                  isChecked={formikProps.values.roleIds.includes(
                                    accessRole.id
                                  )}
                                  isDisabled={!hasPermission("assign.roles")}
                                  title={
                                    hasPermission("assign.roles")
                                      ? undefined
                                      : "You don't have permission to assign roles"
                                  }
                                  onChange={(e) =>
                                    formikProps.setFieldValue(
                                      "roleIds",
                                      e.target.checked
                                        ? [
                                            ...formikProps.values.roleIds,
                                            accessRole.id,
                                          ]
                                        : formikProps.values.roleIds.filter(
                                            (id: number) =>
                                              id !== accessRole.id
                                          )
                                    )
                                  }
                                />
                              </Flex>
                            ))}
                          </Box>
                        ),
                      },
                    ]}
                  />

                  {(activeTab === 1 || mode === "edit") &&
                    hasDivisionMissingATeam && (
                    <Paragraph color="red.primary" fontSize={1} mt={2} mb={0}>
                      Select at least one Team for every Division that has
                      Teams before continuing.
                    </Paragraph>
                  )}

                  <Box mt={4}>
                    {/* Invite walks the tabs with Next and only sends from
                        the last one. Edit shows Save & Next on every tab but
                        the last — it saves (if anything changed) and moves
                        on — and Save on the last, which closes. */}
                    {mode === "edit" && activeTab < 2 ? (
                      // type="button" + submitForm() rather than a submit
                      // button, so a click with nothing changed can just
                      // advance without hitting the API.
                      <Button
                        key="save-next-button"
                        type="button"
                        size="large"
                        width="100%"
                        mt="4"
                        mb="0"
                        onClick={() => {
                          if (formikProps.dirty) {
                            formikProps.submitForm();
                          } else if (activeTab === 0) {
                            handleNextFromDetails();
                          } else {
                            setActiveTab(activeTab + 1);
                          }
                        }}
                        isDisabled={
                          hasDivisionMissingATeam ||
                          isLoadingAction ||
                          isSyncingRoles ||
                          (formikProps.dirty &&
                            (!formikProps.isValid ||
                              !hasPermission("users.manage")))
                        }
                        title={
                          hasPermission("users.manage")
                            ? undefined
                            : "You don't have permission to manage users"
                        }
                      >
                        {isLoadingAction || isSyncingRoles
                          ? "Saving..."
                          : "Save & Next"}
                      </Button>
                    ) : activeTab < 2 ? (
                      // Distinct `key` from the submit button below is
                      // load-bearing, not cosmetic: both render a <button>
                      // at this same tree position, and without separate
                      // keys React reuses the same DOM node across renders
                      // and just flips its `type` attribute (button ->
                      // submit) instead of unmounting/remounting it. Doing
                      // that flip within the same click's event cycle can
                      // make the browser treat that very click's native
                      // default action as a submit, firing Save/Send Invite
                      // immediately instead of just advancing the tab — this
                      // is exactly why Next on Divisions & Teams used to jump
                      // straight to saving instead of reaching Role first.
                      <Button
                        key="next-button"
                        type="button"
                        size="large"
                        width="100%"
                        mt="4"
                        mb="0"
                        onClick={
                          activeTab === 0
                            ? handleNextFromDetails
                            : () => setActiveTab(2)
                        }
                        isDisabled={activeTab === 1 && hasDivisionMissingATeam}
                      >
                        Next
                      </Button>
                    ) : (
                      <Button
                        key="submit-button"
                        type="submit"
                        size="large"
                        width="100%"
                        mt="4"
                        mb="0"
                        isDisabled={
                          !formikProps.dirty ||
                          (formikProps.dirty && !formikProps.isValid) ||
                          hasDivisionMissingATeam ||
                          isLoadingAction ||
                          isSyncingRoles ||
                          !hasPermission(userId ? "users.manage" : "users.invite")
                        }
                        title={
                          hasPermission(userId ? "users.manage" : "users.invite")
                            ? undefined
                            : userId
                            ? "You don't have permission to manage users"
                            : "You don't have permission to invite users"
                        }
                      >
                        {isLoadingAction || isSyncingRoles
                          ? "Saving..."
                          : mode === "edit"
                          ? "Save"
                          : "Send Invite"}
                      </Button>
                    )}
                  </Box>
                </Form>
              );
            }}
          </Formik>
        )}
      </Box>

      <ConfirmModal
        isOpen={Boolean(pendingSave)}
        onRequestClose={() => setPendingSave(null)}
        handleConfirm={() => {
          if (pendingSave) {
            handleSubmit(pendingSave.values, pendingSave.helpers);
          }
          setPendingSave(null);
        }}
        title="Change this user's access?"
        content={pendingSave?.content}
      />
    </Slider>
  );
};
